import React from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../components/layout/AppLayout';
import { dummyData } from './data';
import ProgressOverview from './ProgressOverview';
import StageSnapshot from './StageSnapshot';
import TodaysWork from './TodaysWork';
import DelayedTasks from './DelayedTasks';
import WorkStopped from './WorkStopped';
import Accountability from './Accountability';
import DelaysAndAlerts from './DelaysAndAlerts';
import MaterialStatus from './MaterialStatus';
import QuickSummary from './QuickSummary';
import PhotoProof from './PhotoProof';
import { ArrowLeft } from 'lucide-react';

// Map dummyData to the shapes expected by each component
const getSampleData = () => {
  const todayProgress = {
    percentage: dummyData.todayProgress.percentage,
    trend: dummyData.todayProgress.trend,
    description: dummyData.todayProgress.description
  };
  const overallProgress = {
    percentage: dummyData.overallProgress.percentage,
    status: dummyData.overallProgress.status
  };
  const stageSnapshotStages = dummyData.stages;
  const todaysWork = dummyData.todaysWork;
  const delayedAtStart = dummyData.delayedTasks.haventStarted;
  const delayedDuringTask = dummyData.delayedTasks.workStopped;
  const delaysAndAlerts = dummyData.delaysAndAlerts.map((a) => ({
    id: a.id,
    title: a.title,
    type: a.type,
    person: a.person,
    description: a.description
  }));
  const quickSummary = {
    text: dummyData.quickSummary.text,
    tomorrowsPlan: dummyData.quickSummary.tomorrowsPlan
  };
  const accountabilityPeople = dummyData.accountability.map((p) => ({
    id: p.id,
    name: p.name,
    role: p.role,
    designation: { name: p.role }
  }));
  const materialStatus = dummyData.materialStatus.map((m) => ({
    itemName: m.name,
    status: m.status,
    used: m.stock,
    unit: ''
  }));
  const photoProofPhotos = dummyData.photoProof.map((p) => ({
    id: p.id,
    title: p.title,
    taskName: p.title,
    time: p.time,
    url: p.url
  }));

  return {
    todayProgress,
    overallProgress,
    stageSnapshotStages,
    todaysWork,
    delayedAtStart,
    delayedDuringTask,
    delaysAndAlerts,
    quickSummary,
    accountabilityPeople,
    materialStatus,
    photoProofPhotos
  };
};

const SampleDailyReport = () => {
  const navigate = useNavigate();
  const data = getSampleData();

  return (
    <Layout>
      <div className="bg-[#F8FAFC] min-h-screen">
        <div className="sticky top-0 z-40 bg-[#EEF3F7] backdrop-blur pt-6 px-6 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/builder/operations/daily-reports')}
                className="flex items-center gap-2 px-3 py-2 text-[#526477] hover:bg-gray-100 rounded-md transition-colors text-sm font-medium"
              >
                <ArrowLeft size={20} />
                Back to Daily Reports
              </button>
              <span className="h-5 w-px bg-[#526477] opacity-50" />
              <h1 className="text-lg font-bold text-[#111827]">Sample Daily Report</h1>
              <span className="px-2 py-0.5 bg-[#1F4B55] text-white text-xs font-semibold rounded">Preview</span>
            </div>
            <div className="text-sm text-[#526477] font-medium">
              Project: {dummyData.projectName} · Date: {dummyData.date}
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 max-w-[1600px] mx-auto">
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2 flex flex-col gap-6">
                <div className="lg:col-span-2 h-full">
                  <ProgressOverview
                    today={data.todayProgress}
                    overall={data.overallProgress}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <TodaysWork work={data.todaysWork} />
                  </div>
                  <div className="flex flex-col gap-6">
                    <DelayedTasks tasks={data.delayedAtStart} />
                    <WorkStopped tasks={data.delayedDuringTask} />
                  </div>
                </div>
                <div>
                  <DelaysAndAlerts alerts={data.delaysAndAlerts} />
                </div>
                <div>
                  <QuickSummary summary={data.quickSummary} />
                </div>
              </div>
              <div className="flex flex-col gap-6 lg:mt-0">
                <div className="lg:col-span-1 h-full">
                  <StageSnapshot stages={data.stageSnapshotStages} />
                </div>
                <div>
                  <Accountability people={data.accountabilityPeople} />
                </div>
                <div>
                  <MaterialStatus materials={data.materialStatus} />
                </div>
                <div>
                  <PhotoProof photos={data.photoProofPhotos} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SampleDailyReport;
